import {
  createContext,
  type MouseEvent,
  type ReactNode,
  useContext,
} from "react";

import { useMediaQuery } from "usehooks-ts";

import { BuiFloatingActionBar } from "@btnet/blueprint-components/BuiFloatingActionBar";
import { IconCaretSmallUp, IconX } from "@btnet/blueprint-icons";
import { cn } from "@btnet/utilities-core";

import { BTButton, type IBTButtonPropsBase } from "../BTButton/BTButton";
import { BTDropdown } from "../BTDropdown/BTDropdown";
import { BTMenu } from "../BTMenu/BTMenu";
import { BTPopover } from "../BTPopover/BTPopover";
import "./BTFloatingActionBar.less";

/**
 * Click handler shared by every FAB action and menu item. May be async — the bar
 * fires the action and does not await the result.
 */
export type TBTFloatingActionBarClickHandler = (
  event?: MouseEvent<HTMLElement>,
) => void | Promise<void>;

/**
 * The shared shape of a FAB action ({@link IBTFloatingActionBarActionProps},
 * {@link IBTFloatingActionBarIconActionProps}). It inherits `BTButton`'s props
 * (variant, disabled, etc.), minus the ones the bar owns itself — the testid
 * (derived from `id`), the icon/label content, the className, and the responsive
 * collapse.
 */
interface IBTFloatingActionBarActionBase
  extends Omit<
    IBTButtonPropsBase,
    "data-testid" | "children" | "icon" | "className" | "collapse" | "onClick"
  > {
  /**
   * Stable unique id for the action. Used as the React key and to derive the
   * action's testid, so it must be unique within a single bar.
   */
  id: string;

  /**
   * The action's label. Rendered inline (as button text, or as the tooltip +
   * accessible name for an icon-only action) and as the menu-item text when the
   * bar collapses.
   */
  children: ReactNode;

  /** Click handler invoked when the action is triggered (inline or collapsed). */
  onClick: TBTFloatingActionBarClickHandler;

  /**
   * When true, the action renders in the danger (red) style — both inline and in
   * its collapsed menu item.
   * @default false
   */
  danger?: boolean;

  testid?: string;
}

/**
 * Props for {@link BTFloatingActionBar} — the app-facing, list-page floating
 * action bar. The root shows automatically while `selectedCount > 0` and always
 * renders the built-in leading group — the deselect (X) button, the
 * "{count} selected" label, and a divider. The actions are composed from the sub-components
 * ({@link BTFloatingActionBar.IconAction}, {@link BTFloatingActionBar.Action},
 * {@link BTFloatingActionBar.Divider}, {@link BTFloatingActionBar.MoreActions}).
 *
 * At/above the `md` breakpoint the inline actions and the optional `MoreActions`
 * dropdown are shown; below `md` every action merges into a single, text-only
 * combined "Actions" dropdown.
 */
export interface IBTFloatingActionBarProps {
  /**
   * Number of currently-selected items. The bar is shown (with its slide/fade
   * animation) while this is greater than zero and hidden once it reaches zero.
   * Also drives the built-in "{count} selected" label.
   */
  selectedCount: number;

  /**
   * Callback triggered when the user clicks the built-in deselect (X) button.
   * Consumers should clear their selection in response.
   */
  onDeselect: () => void;

  /**
   * The bar's actions — compose them from the `BTFloatingActionBar.*`
   * sub-components (`IconAction`, `Action`, `Divider`, `MoreActions`). The
   * leading deselect + count label are built in and must not be passed here.
   */
  children: ReactNode;

  /**
   * Accessible label for the bar's toolbar region. Defaults to `"Grid actions"`.
   * @default "Grid actions"
   */
  ariaLabel?: string;

  /**
   * Additional class name(s) to apply to the bar.
   */
  className?: string;

  /**
   * The required data-testid so it can be granular for testing. Sub-components
   * derive their own testids from this (e.g. `"{testid}-deselect"`,
   * `"{testid}-{actionId}"`).
   */
  testid: string;
}

/**
 * Props for {@link BTFloatingActionBar.Action} — a text bulk action with an
 * optional leading icon. Inline (at/above `md`) it renders as a text button
 * ("{icon} {label}"); when the bar collapses below `md` it becomes a text-only
 * menu item in the combined "Actions" dropdown (the icon is dropped). Use this
 * for the higher-priority, always-labeled actions (e.g. "Send", "Move",
 * "Mark ready for payment"). For icon-only inline actions, use
 * {@link BTFloatingActionBar.IconAction}.
 */
export interface IBTFloatingActionBarActionProps
  extends IBTFloatingActionBarActionBase {
  /**
   * Optional leading icon shown before the label inline. It is dropped when the
   * bar collapses (the dropdown is text-only).
   */
  icon?: ReactNode;

  /**
   * Hover/focus tooltip for the inline button — typically an explanation of why
   * the action is unavailable. Dropped when the bar collapses (the menu is
   * text-only).
   *
   * Note this is additive to the label: unlike
   * {@link BTFloatingActionBar.IconAction}, whose `children` become its tooltip,
   * an `Action` already shows its label inline, so a tooltip is only rendered
   * when this prop is set.
   */
  tooltip?: ReactNode;
}

/**
 * Props for {@link BTFloatingActionBar.IconAction} — an icon-only bulk action.
 * Inline (at/above `md`) it renders icon-only, with the label (`children`)
 * surfaced as a hover/focus tooltip and used as the accessible name; when the
 * bar collapses below `md` it becomes a text-only menu item in the combined
 * "Actions" dropdown. Use this for the compact, icon-only actions (e.g. Print,
 * Copy, Delete). For always-labeled actions, use
 * {@link BTFloatingActionBar.Action}.
 */
export interface IBTFloatingActionBarIconActionProps
  extends IBTFloatingActionBarActionBase {
  /**
   * The icon shown inline. Required — this is the icon-only action.
   */
  icon: ReactNode;
}

/**
 * Props for {@link BTFloatingActionBar.Divider} — a vertical separator between
 * groups of inline actions. Stays inline and is dropped when the bar collapses.
 */
export interface IBTFloatingActionBarDividerProps {
  /**
   * Additional class name(s) to apply to the divider rule.
   */
  className?: string;
}

/**
 * Props for {@link BTFloatingActionBar.MoreActions} — the explicit "More
 * actions" overflow dropdown shown at the end of the bar. Compose it from
 * {@link BTFloatingActionBar.MoreActionsItem} children — plus
 * {@link BTFloatingActionBar.MoreActionsSubMenu} for nested, labeled groups and
 * `BTFloatingActionBar.Divider` to separate them. While the bar is expanded it
 * renders its own trigger + menu.
 *
 * The expanded trigger is a button showing `label` (default "More actions")
 * followed by an upward caret. Three knobs adjust it:
 * - **`icon`:** a leading icon, rendered through the button's `icon` prop (like
 *   the inline {@link BTFloatingActionBar.IconAction}s). Pass `icon` with no
 *   `label` for an icon-only trigger; pair it with `ariaLabel` for the name.
 * - **`label` as a `ReactNode`:** custom trigger content (usually text).
 * - **`caret={false}`:** drop the trailing caret so an icon-only trigger sits
 *   flush with the surrounding icon actions.
 *
 * When the bar collapses below the `md` breakpoint, behavior depends on `label`
 * (the `icon` is dropped — the collapsed menu is text-only):
 * - **Default (no `label`):** the items flatten into the single combined
 *   "Actions" dropdown alongside the inline actions.
 * - **Custom label (e.g. "Attach to"):** the group is preserved as a labeled
 *   submenu inside the combined "Actions" dropdown.
 *
 * A bar may contain more than one `MoreActions` group (e.g. a "New" dropdown
 * next to the "More actions" overflow). When it does, give each group a unique
 * `id` so their triggers/menus get distinct testids and don't collide.
 */
export interface IBTFloatingActionBarMoreActionsProps {
  /**
   * The menu contents — {@link BTFloatingActionBar.MoreActionsItem} children,
   * `BTFloatingActionBar.Divider`s between groups, and
   * {@link BTFloatingActionBar.MoreActionsSubMenu}s for nested, labeled groups.
   */
  children: ReactNode;

  /**
   * Unique id for this group, used as the testid suffix for its trigger/menu
   * (`{root}-{id}-trigger`, `{root}-{id}-menu`) and, when collapsed, for its
   * submenu. Required to be unique when a bar has multiple `MoreActions` groups.
   * @default "more"
   */
  id?: string;

  /**
   * The trigger's leading icon, rendered through the button's `icon` prop so it
   * matches the inline {@link BTFloatingActionBar.IconAction}s. Pass `icon` alone
   * (no `label`) for an icon-only trigger — set `ariaLabel` for its accessible
   * name and usually `caret={false}` so it sits flush with the icon actions. The
   * icon is dropped when the bar collapses (the combined dropdown is text-only).
   */
  icon?: ReactNode;

  /**
   * The trigger's text/content while the bar is expanded. Providing a custom
   * label also preserves the group as a labeled submenu when the bar collapses;
   * with no label the group flattens on collapse. Falls back to "More actions"
   * only when no `icon` is given either.
   *
   * A `ReactNode`, but when it isn't plain text set `ariaLabel` so the trigger
   * still has an accessible name.
   * @default "More actions"
   */
  label?: ReactNode;

  /**
   * Accessible name for the trigger button. Supply this whenever the trigger has
   * no plain-text label (e.g. an icon-only trigger), so it is still labeled for
   * assistive tech.
   */
  ariaLabel?: string;

  /**
   * Whether the expanded trigger shows the trailing upward caret. Set `false` for
   * an icon-only trigger so it sits flush with the inline icon actions.
   * @default true
   */
  caret?: boolean;

  /**
   * When true, the group's trigger renders disabled and its menu cannot be
   * opened. Use it to gate a whole group while its action is in flight (e.g. a
   * bulk update is saving). When the bar collapses, the group's submenu row is
   * disabled instead; a group that flattens (no `label`) has no row of its own,
   * so disable its items individually in that case.
   * @default false
   */
  disabled?: boolean;

  /**
   * Overrides the derived `{barTestid}-{id}` base testid for this group, so its
   * trigger and menu read as `{testid}-trigger` / `{testid}-menu` and its items
   * as `{testid}-{itemId}`.
   *
   * Only needed to preserve a pre-existing selector. New code should omit this.
   */
  testid?: string;
}

/**
 * Props for {@link BTFloatingActionBar.MoreActionsSubMenu} — a nested, labeled
 * group inside a {@link BTFloatingActionBar.MoreActions} dropdown. Its `title`
 * row opens a flyout containing the group's children (further
 * {@link BTFloatingActionBar.MoreActionsItem}s, `Divider`s, or another
 * `MoreActionsSubMenu`).
 *
 * Use it when a set of overflow items belongs under one heading — e.g. an
 * "Attach to" group of entity targets — instead of flattening them into the
 * parent menu. It behaves the same whether the bar is expanded (inside the
 * group's own dropdown) or collapsed (inside the combined "Actions" dropdown),
 * so no responsive handling is needed at the call site.
 */
export interface IBTFloatingActionBarMoreActionsSubMenuProps {
  /**
   * Stable unique id for the submenu. Used to derive its testid
   * (`{menu}-{id}`) and the testids of the items inside it
   * (`{menu}-{id}-{itemId}`), so it must be unique within its parent menu.
   */
  id: string;

  /**
   * The submenu's row content — the label shown in the parent menu, next to the
   * expand arrow. When it isn't plain text, the row has no accessible name of
   * its own, so prefer text here.
   */
  title: ReactNode;

  /**
   * The submenu's contents — {@link BTFloatingActionBar.MoreActionsItem}
   * children, `BTFloatingActionBar.Divider`s, or nested
   * {@link BTFloatingActionBar.MoreActionsSubMenu}s.
   */
  children: ReactNode;

  /**
   * Optional leading icon for the submenu row.
   */
  icon?: ReactNode;

  /**
   * When true, the row renders disabled and its flyout cannot be opened.
   * @default false
   */
  disabled?: boolean;

  /**
   * Hover tooltip for the submenu row — typically an explanation of why the
   * group is currently unavailable. Pair it with `disabled` so a greyed-out row
   * still explains itself.
   *
   * Rendered as a native `title` on the row's content, so it is plain text only.
   * (Unlike {@link BTFloatingActionBar.MoreActionsItem}, the tooltip cannot go
   * on the menu node itself — `title` is already this component's row content.)
   */
  tooltip?: string;

  /**
   * Overrides the derived `{menuTestid}-{id}` base testid for this submenu, so
   * its items read as `{testid}-{itemId}`.
   *
   * Only needed to preserve a pre-existing selector. New code should omit this.
   */
  testid?: string;
}

/**
 * Props for {@link BTFloatingActionBar.MoreActionsItem} — a single item inside
 * the {@link BTFloatingActionBar.MoreActions} dropdown, or inside a
 * {@link BTFloatingActionBar.MoreActionsSubMenu} nested within it.
 */
export interface IBTFloatingActionBarMoreActionsItemProps {
  /**
   * Stable unique id for the item. Used as the React key and to derive the
   * item's testid, so it must be unique within a single bar.
   */
  id: string;

  /**
   * The item's label.
   */
  children: ReactNode;

  /** Click handler invoked when the item is selected. */
  onClick: TBTFloatingActionBarClickHandler;

  /**
   * Optional leading icon for the item.
   */
  icon?: ReactNode;

  /**
   * When true, the item renders in the danger (red) style — e.g. "Void".
   * @default false
   */
  danger?: boolean;

  /**
   * When true, the item renders disabled and its click handler is not called.
   * @default false
   */
  disabled?: boolean;

  /**
   * Hover tooltip for the item — typically an explanation of why the action is
   * currently unavailable (e.g. "Loading invoice details…" while a lookup runs).
   * Pair it with `disabled` so a greyed-out item still explains itself.
   *
   * Rendered as the menu item's native `title`, so it is plain text only.
   */
  tooltip?: string;

  /**
   * Overrides the derived `{menuTestid}-{id}` testid with this exact value.
   *
   * Only needed to preserve a pre-existing selector that used the action's raw
   * testid. New code should omit this and take the derived testid.
   */
  testid?: string;
}

// Lets sub-components read the bar's testid and current collapse state without
// prop-drilling or child cloning. Each sub-component renders itself for the
// active layout — an inline control when expanded, a menu node when collapsed —
// so consumers just compose the tree and the layout resolves through context.
interface IFabContext {
  testid: string;
  isCollapsed: boolean;
  /**
   * The testid prefix for items rendered inside an overflow/combined dropdown
   * menu. Set by {@link OverflowDropdown} to its own testid so a menu item reads
   * as `{menuTestid}-{id}` (e.g. `{root}-more-{id}` in the expanded overflow,
   * `{root}-actions-{id}` in the collapsed combined menu). Undefined for inline
   * actions, which key off the root `testid` instead.
   */
  menuTestid?: string | undefined;
}

const FabContext = createContext<IFabContext>({
  testid: "",
  isCollapsed: false,
});

// The vertical rule between inline groups. mx-2 (8px) on top of the bar's 8px
// gap yields ~16px on each side. Shared by the composable `Divider` and the
// root's built-in leading divider.
const FabDividerRule = ({ className }: { className?: string | undefined }) => (
  <div
    aria-hidden="true"
    className={cn("mx-2 h-8 self-center border-l border-blue-5", className)}
  />
);

// The one menu item shape every action collapses to. `Action`, `IconAction`,
// and `MoreActionsItem` all render this — a text-only item keyed off the action
// `id`, dropping any inline-only affordances (icons, tooltips).
const FabMenuItem = ({
  testid,
  id,
  testidOverride,
  children,
  onClick,
  icon,
  danger = false,
  disabled = false,
  tooltip,
}: {
  testid: string;
  id: string;
  /** When set, used verbatim instead of the derived `{testid}-{id}`. */
  testidOverride?: string | undefined;
  children: ReactNode;
  onClick: TBTFloatingActionBarClickHandler;
  icon?: ReactNode;
  danger?: boolean;
  disabled?: boolean;
  tooltip?: string | undefined;
}) => (
  <BTMenu.Item
    // See the note on MoreActionsSubMenu: wrapped menu nodes must carry
    // `eventKey` explicitly, or every sibling shares one rc-menu identity.
    key={testidOverride ?? `${testid}-${id}`}
    eventKey={testidOverride ?? `${testid}-${id}`}
    testid={testidOverride ?? `${testid}-${id}`}
    icon={icon}
    danger={danger}
    disabled={disabled}
    // antd renders `title` as the item's native hover tooltip. A disabled item
    // swallows pointer events, so the browser shows it from the wrapper.
    title={tooltip}
    // BuiMenu hardcodes the item text color (incl. on :hover), swallowing antd's
    // built-in danger red. Re-assert it with a utility: the Tailwind `utilities`
    // layer beats BuiMenu's app-layer rule in every state, so no :hover override
    // or !important is needed.
    className={cn({ "text-red-70": danger })}
    onClick={() => void onClick()}
  >
    {children}
  </BTMenu.Item>
);

/**
 * A text bulk action with an optional leading icon. Inline (at/above `md`) it
 * renders as a text button ("{icon} {label}"); when the bar collapses below `md`
 * it renders itself as a text-only item in the combined "Actions" dropdown (the
 * icon is dropped). Use for higher-priority, always-labeled actions. For
 * icon-only inline actions, see {@link BTFloatingActionBarIconAction}.
 *
 * @example
 * <BTFloatingActionBar.Action id="send" icon={<IconPaperPlaneRight />} onClick={onSend}>
 *   Send
 * </BTFloatingActionBar.Action>
 */
const BTFloatingActionBarAction = ({
  id,
  children,
  onClick,
  icon,
  tooltip,
  type = "tertiary",
  danger = false,
  disabled = false,
  testid: testidOverride,
  ...buttonProps
}: IBTFloatingActionBarActionProps) => {
  const { testid, isCollapsed, menuTestid } = useContext(FabContext);

  // Collapsed: a text-only menu item in the root's combined dropdown, keyed off
  // that dropdown's testid (`{root}-actions-{id}`).
  if (isCollapsed) {
    return (
      <FabMenuItem
        testid={menuTestid ?? testid}
        id={id}
        testidOverride={testidOverride}
        onClick={onClick}
        danger={danger}
        disabled={disabled}
      >
        {children}
      </FabMenuItem>
    );
  }

  const button = (
    <BTButton
      {...buttonProps}
      data-testid={testidOverride ?? `${testid}-${id}`}
      type={type}
      onClick={(event) => void onClick(event)}
      icon={icon}
      disabled={disabled}
      className={cn({ "BTFloatingActionBar-danger": danger })}
    >
      {children}
    </BTButton>
  );

  // Unlike IconAction, the label already shows inline, so only wrap in a popover
  // when there's extra tooltip content to surface.
  return tooltip ? (
    <BTPopover content={tooltip} placement="top">
      {button}
    </BTPopover>
  ) : (
    button
  );
};

/**
 * An icon-only bulk action. Inline (at/above `md`) it renders icon-only, with
 * the label (`children`) shown as a hover/focus tooltip (via `BTPopover`) and
 * used as the accessible name; when the bar collapses below `md` it renders
 * itself as a text-only item in the combined "Actions" dropdown. Use for
 * compact, icon-only actions. For always-labeled actions, see
 * {@link BTFloatingActionBarAction}.
 *
 * @example
 * <BTFloatingActionBar.IconAction id="delete" icon={<IconTrash />} onClick={onDelete}>
 *   Delete
 * </BTFloatingActionBar.IconAction>
 */
const BTFloatingActionBarIconAction = ({
  id,
  children,
  icon,
  onClick,
  type = "tertiary",
  danger = false,
  disabled = false,
  testid: testidOverride,
  ...buttonProps
}: IBTFloatingActionBarIconActionProps) => {
  const { testid, isCollapsed, menuTestid } = useContext(FabContext);

  // Collapsed: a text-only menu item (the icon is dropped), keyed off the
  // combined dropdown's testid (`{root}-actions-{id}`).
  if (isCollapsed) {
    return (
      <FabMenuItem
        testid={menuTestid ?? testid}
        id={id}
        testidOverride={testidOverride}
        onClick={onClick}
        danger={danger}
        disabled={disabled}
      >
        {children}
      </FabMenuItem>
    );
  }

  return (
    <BTPopover content={children} placement="top">
      <BTButton
        {...buttonProps}
        data-testid={testidOverride ?? `${testid}-${id}`}
        type={type}
        onClick={(event) => void onClick(event)}
        icon={icon}
        disabled={disabled}
        aria-label={typeof children === "string" ? children : undefined}
        className={cn({ "BTFloatingActionBar-danger": danger })}
      />
    </BTPopover>
  );
};

/**
 * A separator between groups of actions. Inline (at/above `md`) it renders a
 * vertical rule between inline groups; inside any menu — a `MoreActions`
 * dropdown, a `MoreActionsSubMenu` flyout, or the collapsed combined dropdown —
 * it renders a horizontal `BTMenu.Divider` at the same position.
 */
const BTFloatingActionBarDivider = ({
  className,
}: IBTFloatingActionBarDividerProps) => {
  const { isCollapsed, menuTestid } = useContext(FabContext);

  // `menuTestid` is only set inside a dropdown/submenu, so it doubles as "am I
  // in a menu?" — a divider there must be horizontal even while the bar is
  // expanded (e.g. grouping items within the "More actions" menu).
  if (isCollapsed || menuTestid !== undefined) {
    // bg-gray-20 is the border-default color; the Tailwind `utilities` layer
    // beats BuiMenu's app-layer default fill, so no .less override is needed.
    return <BTMenu.Divider className="bg-gray-20" />;
  }

  return <FabDividerRule className={className} />;
};

/**
 * A single item inside a {@link BTFloatingActionBar.MoreActions} group. Renders
 * a `BTMenu.Item` — inside its group's own dropdown while the bar is expanded,
 * or inside the root's combined "Actions" dropdown once it collapses.
 */
const BTFloatingActionBarMoreActionsItem = ({
  id,
  children,
  onClick,
  icon,
  danger = false,
  disabled = false,
  tooltip,
  testid: testidOverride,
}: IBTFloatingActionBarMoreActionsItemProps) => {
  const { testid, menuTestid } = useContext(FabContext);

  return (
    <FabMenuItem
      // Menu items key off the containing dropdown's testid (`{root}-more` when
      // expanded, `{root}-actions` when collapsed) so they read as
      // `{root}-more-{id}` / `{root}-actions-{id}`, falling back to the root.
      testid={menuTestid ?? testid}
      id={id}
      testidOverride={testidOverride}
      onClick={onClick}
      icon={icon}
      danger={danger}
      disabled={disabled}
      tooltip={tooltip}
    >
      {children}
    </FabMenuItem>
  );
};

/**
 * A nested, labeled group inside a {@link BTFloatingActionBar.MoreActions}
 * dropdown. Renders a `BTMenu.SubMenu` whose row opens a flyout containing the
 * group's children.
 *
 * Unlike a labeled `MoreActions` group — which is a *trigger* on the bar when
 * expanded and only becomes a submenu once the bar collapses — this is always a
 * submenu, in both layouts. Nesting is unbounded: a `MoreActionsSubMenu` may
 * contain another.
 *
 * @example
 * <BTFloatingActionBar.MoreActions>
 *   <BTFloatingActionBar.MoreActionsItem id="recall" onClick={onRecall}>
 *     Recall
 *   </BTFloatingActionBar.MoreActionsItem>
 *   <BTFloatingActionBar.MoreActionsSubMenu id="attach" title="Attach to">
 *     <BTFloatingActionBar.MoreActionsItem id="bid" onClick={onAttachBid}>
 *       Bid packages
 *     </BTFloatingActionBar.MoreActionsItem>
 *   </BTFloatingActionBar.MoreActionsSubMenu>
 * </BTFloatingActionBar.MoreActions>
 */
const BTFloatingActionBarMoreActionsSubMenu = ({
  id,
  title,
  children,
  icon,
  disabled = false,
  tooltip,
  testid: testidOverride,
}: IBTFloatingActionBarMoreActionsSubMenuProps) => {
  const parent = useContext(FabContext);
  // Like every menu node, the submenu keys off its containing menu — the
  // expanded group (`{root}-more`), the collapsed combined menu
  // (`{root}-actions`), or an enclosing submenu when nested.
  const submenuTestid =
    testidOverride ?? `${parent.menuTestid ?? parent.testid}-${id}`;

  return (
    <BTMenu.SubMenu
      // rc-menu identifies each node by `eventKey`. antd normally derives it
      // from the React `key`, but that only works for direct children of a
      // Menu — these nodes are wrapped (BTMenu.SubMenu -> BuiMenu.SubMenu), so
      // the key is consumed by React and never reaches rc-menu, leaving every
      // sibling with the same `-undefined` id: hovering one marked them all
      // active and opened the wrong flyout. Pass it through explicitly.
      key={submenuTestid}
      eventKey={submenuTestid}
      testid={submenuTestid}
      disabled={disabled}
      // `title` is the row's content, so a tooltip cannot ride on the menu node
      // itself the way it does for MoreActionsItem — wrap the content instead.
      title={
        icon || tooltip ? (
          <span
            className={cn({ "flex items-center gap-1": Boolean(icon) })}
            title={tooltip}
          >
            {icon}
            {title}
          </span>
        ) : (
          title
        )
      }
      // The flyout is portaled outside the parent menu, so it doesn't inherit
      // the root menu's class — apply the same styling hook to both.
      popupClassName="BTFloatingActionBar-menu"
    >
      {/* Items inside the flyout key off this submenu, so they read as
          `{menu}-{id}-{itemId}` and stay unique across sibling submenus. */}
      <FabContext.Provider value={{ ...parent, menuTestid: submenuTestid }}>
        {children}
      </FabContext.Provider>
    </BTMenu.SubMenu>
  );
};

/**
 * The dropdown trigger + menu used both for the expanded "More actions" group
 * and the root's collapsed combined "Actions" menu. Its children self-render as
 * `BTMenu.Item`s / `BTMenu.SubMenu`s. The menu opens upward, so the trigger
 * caret points up.
 *
 * The trigger renders an optional leading `icon` (through the button's `icon`
 * prop, matching the inline icon actions), the `triggerLabel` content, and, by
 * default, a trailing upward caret. Set `caret={false}` to drop the caret — e.g.
 * for an icon-only trigger that should sit flush with the surrounding icon
 * actions. Pass `ariaLabel` to name the trigger when it has no text label.
 */
const OverflowDropdown = ({
  testid,
  triggerLabel,
  icon,
  ariaLabel,
  caret = true,
  disabled = false,
  children,
}: {
  testid: string;
  triggerLabel?: ReactNode;
  icon?: ReactNode;
  ariaLabel?: string | undefined;
  caret?: boolean | undefined;
  disabled?: boolean | undefined;
  children: ReactNode;
}) => {
  const parent = useContext(FabContext);

  return (
    <BTDropdown
      testid={`${testid}-dropdown`}
      placement="topCenter"
      // Gate the dropdown itself, not just the button: a disabled BTButton stops
      // click events, but the menu must not open via keyboard either.
      disabled={disabled}
      overlay={
        <BTMenu
          testid={`${testid}-menu`}
          aria-label="More options"
          className="BTFloatingActionBar-menu"
        >
          {/* Items inside this menu self-render off `menuTestid`, so they read as
              `{testid}-{id}` (e.g. `{root}-more-{id}` / `{root}-actions-{id}`). */}
          <FabContext.Provider value={{ ...parent, menuTestid: testid }}>
            {children}
          </FabContext.Provider>
        </BTMenu>
      }
      trigger={["click"]}
    >
      <BTButton
        data-testid={`${testid}-trigger`}
        type="tertiary"
        icon={icon}
        aria-label={ariaLabel}
        disabled={disabled}
      >
        {(triggerLabel || caret) && (
          <span className="flex items-center gap-1 whitespace-nowrap">
            {triggerLabel}
            {caret && <IconCaretSmallUp />}
          </span>
        )}
      </BTButton>
    </BTDropdown>
  );
};

/**
 * A dropdown menu group in the bar. While the bar is expanded it renders its
 * own trigger + menu ("More actions ▴" by default, or a custom `label` like
 * "New ▴"). Pass an `icon` (rendered through the button's `icon` prop, like the
 * inline icon actions) for an icon-only trigger — with `ariaLabel` for the
 * accessible name and `caret={false}` so it sits flush with the icon actions.
 *
 * Once the bar collapses below `md`, it folds into the root's combined "Actions"
 * dropdown: a default group (no `label`) flattens its items in place, while a
 * custom-labeled group (e.g. "Attach to") is preserved as a labeled submenu.
 *
 * A bar may contain multiple groups — e.g. a "New" dropdown next to the "More
 * actions" overflow. Give each a unique `id` so their testids don't collide.
 *
 * @example
 * <BTFloatingActionBar.MoreActions id="new" label="New">
 *   <BTFloatingActionBar.MoreActionsItem id="bill" onClick={onNewBill}>
 *     Bill
 *   </BTFloatingActionBar.MoreActionsItem>
 * </BTFloatingActionBar.MoreActions>
 * <BTFloatingActionBar.MoreActions
 *   id="export"
 *   icon={<IconDownloadSimple />}
 *   ariaLabel="Export"
 *   caret={false}
 * >
 *   <BTFloatingActionBar.MoreActionsItem id="csv" onClick={onExportCsv}>
 *     CSV
 *   </BTFloatingActionBar.MoreActionsItem>
 * </BTFloatingActionBar.MoreActions>
 * <BTFloatingActionBar.MoreActions>
 *   <BTFloatingActionBar.MoreActionsItem id="void" danger onClick={onVoid}>
 *     Void
 *   </BTFloatingActionBar.MoreActionsItem>
 *   <BTFloatingActionBar.MoreActionsItem id="attach" onClick={onAttach}>
 *     Attach files
 *   </BTFloatingActionBar.MoreActionsItem>
 * </BTFloatingActionBar.MoreActions>
 */
const BTFloatingActionBarMoreActions = ({
  children,
  icon,
  label,
  ariaLabel,
  caret,
  disabled = false,
  id = "more",
  testid: testidOverride,
}: IBTFloatingActionBarMoreActionsProps) => {
  const { testid, isCollapsed } = useContext(FabContext);
  const groupTestid = testidOverride ?? `${testid}-${id}`;

  if (isCollapsed) {
    // A custom label keeps the group as a labeled submenu in the combined menu;
    // the default (no label) flattens the items in place. The icon is dropped —
    // the collapsed menu is text-only, like every other action.
    if (label === undefined) {
      return <>{children}</>;
    }
    return (
      // Keyed for the same reason as MoreActionsSubMenu — see the note there.
      <BTMenu.SubMenu
        key={groupTestid}
        eventKey={groupTestid}
        testid={groupTestid}
        title={label}
        disabled={disabled}
      >
        {children}
      </BTMenu.SubMenu>
    );
  }

  // Expanded: this group's items under its own trigger. `id` keeps the testids
  // distinct when a bar has more than one MoreActions group. Fall back to the
  // "More actions" text only when there's no icon to stand in for it.
  return (
    <OverflowDropdown
      testid={groupTestid}
      icon={icon}
      triggerLabel={label ?? (icon ? undefined : "More actions")}
      ariaLabel={ariaLabel}
      caret={caret}
      disabled={disabled}
    >
      {children}
    </OverflowDropdown>
  );
};

/**
 * BTFloatingActionBar - the app-facing, list-page floating action bar.
 *
 * A composable compound component: the root owns open/visibility and the
 * responsive collapse, while its content
 * is assembled from the sub-components — `Action`, `IconAction`, `Divider`, and
 * `MoreActions` / `MoreActionsItem` / `MoreActionsSubMenu`. Each renders itself for the
 * active layout: at/above the `md` breakpoint the inline `Action`s show on the
 * left and the optional `MoreActions` dropdown on the right; below `md` every
 * action renders as an item inside a single combined "Actions" dropdown, with
 * no loss of any action.
 *
 * @param props - {@link IBTFloatingActionBarProps} controlling selection, actions, and styling.
 * @returns The rendered floating action bar.
 * @example
 * <BTFloatingActionBar
 *   testid="bill-action-bar"
 *   selectedCount={selected.length}
 *   onDeselect={clearSelection}
 * >
 *   <BTFloatingActionBar.IconAction id="print" icon={<IconPrinter />} onClick={onPrint}>
 *     Print
 *   </BTFloatingActionBar.IconAction>
 *   <BTFloatingActionBar.Divider />
 *   <BTFloatingActionBar.Action id="mark-ready" onClick={onMarkReady}>
 *     Mark ready for payment
 *   </BTFloatingActionBar.Action>
 *   <BTFloatingActionBar.MoreActions>
 *     <BTFloatingActionBar.MoreActionsItem id="void" danger onClick={onVoid}>
 *       Void
 *     </BTFloatingActionBar.MoreActionsItem>
 *   </BTFloatingActionBar.MoreActions>
 * </BTFloatingActionBar>
 */

const BTFloatingActionBarInternal = ({
  selectedCount,
  onDeselect,
  children,
  ariaLabel = "Grid actions",
  className,
  testid,
}: IBTFloatingActionBarProps) => {
  const isCollapsed = !useMediaQuery("(min-width: 768px)");

  return (
    <BuiFloatingActionBar
      isOpen={selectedCount > 0}
      ariaLabel={ariaLabel}
      testid={testid}
      className={cn("BTFloatingActionBar gap-2", className)}
    >
      <FabContext.Provider value={{ testid, isCollapsed }}>
        <span className="whitespace-nowrap">{selectedCount} selected</span>
        <BTButton
          data-testid={`${testid}-deselect`}
          type="tertiary"
          aria-label="Uncheck all items"
          onClick={onDeselect}
          icon={<IconX />}
        />
        <FabDividerRule />

        {/* Expanded: children self-render inline. Collapsed: they self-render as
            menu nodes inside a single combined "Actions" dropdown. */}
        {isCollapsed ? (
          <OverflowDropdown testid={`${testid}-actions`} triggerLabel="Actions">
            {children}
          </OverflowDropdown>
        ) : (
          children
        )}
      </FabContext.Provider>
    </BuiFloatingActionBar>
  );
};

/**
 * BTFloatingActionBar with its composable sub-components attached. See
 * {@link BTFloatingActionBarInternal} for usage.
 */
export const BTFloatingActionBar = Object.assign(BTFloatingActionBarInternal, {
  /** A separator between action groups (vertical inline, horizontal in the menu). See {@link BTFloatingActionBarDivider}. */
  Divider: BTFloatingActionBarDivider,
  /** A text action with an optional leading icon. See {@link BTFloatingActionBarAction}. */
  Action: BTFloatingActionBarAction,
  /** An icon-only action (tooltip label). See {@link BTFloatingActionBarIconAction}. */
  IconAction: BTFloatingActionBarIconAction,
  /** The explicit overflow dropdown. See {@link BTFloatingActionBarMoreActions}. */
  MoreActions: BTFloatingActionBarMoreActions,
  /** An item inside the MoreActions dropdown. See {@link BTFloatingActionBarMoreActionsItem}. */
  MoreActionsItem: BTFloatingActionBarMoreActionsItem,
  /** A nested, labeled submenu inside the MoreActions dropdown. See {@link BTFloatingActionBarMoreActionsSubMenu}. */
  MoreActionsSubMenu: BTFloatingActionBarMoreActionsSubMenu,
});
